import useGetUserRole from '@forwardflow/hooks/customHooks/useGetUserRole'
import { ChevronRightIcon } from '@forwardflow/icons'
import Icon from '@forwardflow/ui/Icons'
import cn from 'classnames'
import { motion } from 'framer-motion'
import { useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

const MotionLink = motion.create(Link)

const SettingsMenuItem = ({
   label,
   to,
   isSelected = false,
}: {
   label: string
   to: string
   isSelected: boolean
}) => {
   return (
      <MotionLink
         to={to}
         className={`flex items-center justify-between p-6 pr-3 cursor-pointer w-full ${
            isSelected ? 'bg-[#9061DA]/50' : 'hover:bg-[#9061DA]/10'
         }`}
         whileTap={{ scale: 0.95 }}
      >
         <p className="text-white text-sm font-medium">{label}</p>
         <ChevronRightIcon
            className={cn(
               'text-white w-4 h-4',
               isSelected ? 'opacity-100' : 'opacity-0'
            )}
         />
      </MotionLink>
   )
}

export const SideNav = () => {
   const { data: userRoleData } = useGetUserRole()
   const isAgent = userRoleData?.roles.includes('Agent')
   const location = useLocation()
   const scrollRef = useRef<HTMLDivElement>(null)
   const [isScrolled, setIsScrolled] = useState(false)

   const isActive = (path: string) => location.pathname === path

   const handleScroll = () => {
      if (scrollRef.current) {
         setIsScrolled(scrollRef.current.scrollTop > 0)
      }
   }

   return (
      <div className="flex flex-col w-[350px] shrink-0 rounded-lg bg-[#9061DA]/[0.05] overflow-hidden">
         <div className={cn(
            'flex items-center gap-2 h-20 px-6 shrink-0 transition-shadow duration-200',
            isScrolled && 'shadow-[0_4px_12px_rgba(0,0,0,0.3)]'
         )}>
            <Icon name="Gear" className="fill-white w-5 h-5 shrink-0 [&>svg]:w-5 [&>svg]:h-5 [&>svg]:block" />
            <p className="text-white text-lg font-medium leading-none">Account Settings</p>
         </div>
         <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto"
         >
            {!isAgent && (
               <>
                  <SettingsMenuItem
                     label="User Management"
                     to="/settings/user-management"
                     isSelected={isActive('/settings/user-management')}
                  />
                  <SettingsMenuItem
                     label="Organization Details"
                     to="/settings/organization-details"
                     isSelected={isActive('/settings/organization-details')}
                  />
                  <SettingsMenuItem
                     label="Business Hours"
                     to="/settings/business-hours"
                     isSelected={isActive('/settings/business-hours')}
                  />
                  <SettingsMenuItem
                     label="Appointment Timeframe"
                     to="/settings/appointment-timeframe"
                     isSelected={isActive('/settings/appointment-timeframe')}
                  />
                  <SettingsMenuItem
                     label="Stage Mapping"
                     to="/settings/stage-mapping"
                     isSelected={isActive('/settings/stage-mapping')}
                  />
                  <SettingsMenuItem
                     label="Billing and Subscription"
                     to="/settings/billing"
                     isSelected={isActive('/settings/billing')}
                  />
                  <SettingsMenuItem
                     label="Aria"
                     to="/settings/aria"
                     isSelected={isActive('/settings/aria')}
                  />
               </>
            )}
            {isAgent && (
               <SettingsMenuItem
                  label="Appointment Preference"
                  to="/settings/appointment-preference"
                  isSelected={isActive('/settings/appointment-preference')}
               />
            )}
            <SettingsMenuItem
               label="Integration"
               to="/settings/integration"
               isSelected={isActive('/settings/integration')}
            />
         </div>
      </div>
   )
}
